<template>
  <div>
    <div class="input-field w-full relative flex flex-col justify-center">
      <!-- TITLE -->
      <label
        v-if="title"
        for="title"
        class="text-primary-gray-700 font-inter text-sm font-medium flex mb-1.5"
        :class="[hideTitle ? 'hidden' : '']"
      >
        {{ $t(`placeholder.${title}`) }}
        <span v-if="rules.includes('required')" class="text-text-color-danger pl-1">*</span>
        <!-- TOOLTIP -->
        <div v-if="tooltipProp" class="mt-0.5 md:-mt-0.5">
          <UiOnClickOutside>
            <div
              class="flex-item ml-2 rtl:mr-2 self-center cursor-pointer"
              @click="clickToolTip"
              @mouseover="displayToolTip"
              @mouseleave="hideToolTip"
            >
              <icon
                class="icon"
                icon="infoCircle"
                color="primary-grey"
                :height="height"
                :width="width"
              />
              <div
                v-if="showToolTip"
                class="absolute -ml-4 mt-3 md:mt-2 rtl:-mr-24 md:rtl:-mr-7 z-10"
              >
                <Tooltip
                  class="w-min sm:w-auto"
                  :title="$t(title)"
                  :text="$t(tooltipDesc)"
                  direction="left"
                />
              </div>
            </div>
          </UiOnClickOutside>
        </div>
      </label>
      <!-- INPUT FIELDS -->
      <div class="relative">
        <div
          class="flex text-base border border-primary-gray-300 rounded-lg px-5 focus:outline-none w-full focus-within:shadow-primary-purple"
          :class="[
            errorMessage
              ? 'border-error-500 bg-error-50 focus-within:shadow-error-200'
              : 'bg-white',
            inputHeight,
          ]"
        >
          <icon v-if="icon" class="flex items-center" :icon="iconType" />
          <textarea
            v-if="type === 'textarea'"
            v-show="!hidden"
            :id="id"
            v-model="fieldValue"
            :type="type"
            :name="name === '' ? title : name"
            :rows="rows"
            :resize="resize"
            :placeholder="isTranslated ? $t(`placeholder.${placeholder}`) : placeholder"
            :class="[
              {
                'cursor-not-allowed bg-disable-color': disabled,
                'bg-bg-error': !!errorMessage,
              },
              fieldType,
            ]"
            class="w-full focus:outline-none pt-4.5 pb-2.5"
            :autocomplete="autocomplete"
            :disabled="disabled"
            @change="updateInput($even)"
            @keyup="updateInput($event)"
          ></textarea>
          <!-- TEXT -->
          <input
            v-else
            v-show="!hidden"
            :id="id"
            v-model="fieldValue"
            :name="name === '' ? title : name"
            :type="type"
            min="1"
            :placeholder="isTranslated ? $t(`placeholder.${placeholder}`) : placeholder"
            class="w-full focus:outline-none h-full border-none"
            :class="[
              {
                'cursor-not-allowed bg-disable-color': disabled,
                'bg-bg-error': !!errorMessage,
                'pl-2': !!iconType && icon,
              },
              fieldType,
              childClass,
            ]"
            :autocomplete="autocomplete"
            :disabled="disabled"
            @blur="blurInput"
            @focus="$emit('focus')"
            @input="updateInput($even, 'change')"
          />
          <!-- Domain name -->
          <div
            v-if="name === 'Domain'"
            class="flex justify-between items-center font-medium text-primary-gray-500"
          >
            .{{ getHostName }}
          </div>
          <!-- TIME -->
          <div v-if="type === 'time'" class="input-icon flex justify-between items-center">
            <i class="fas fa-clock" />
          </div>
          <!-- DATE -->
          <div v-if="type === 'date'" class="input-icon flex justify-between items-center">
            <i class="fa fa-calendar" aria-hidden="true" />
          </div>
          <!-- PASSWORD -->
          <div v-if="type === 'password'" class="pl-1 flex items-center">
            <span class="cursor-pointer" @click="showPass">
              <icon
                :key="`password-${forceRender}`"
                class="icon"
                :icon="showText ? 'eye' : 'eye-slash'"
                color="text-placeholder"
                height="17.5"
                width="21.31"
              />
            </span>
          </div>
          <!-- LOADING -->
          <div v-if="isComponentInLoading" class="ml-auto justify-end float-right relative">
            <div
              class="absolute"
              :class="type !== 'file' ? 'top-4 right-2.5' : 'right-2.5 bottom-4'"
            >
              <Loader :is-background-light="true" />
            </div>
          </div>
        </div>
        <div class="text-error-500 min-h-[20px] text-xs italic">
          <p v-if="errorMessage" class="text-right">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tooltipMixin from '@/src/mixins/tooltip-mixin.js'
import UiOnClickOutside from '@src/components/misc/UiOnClickOutside.vue'
import Loader from '@src/views/SignUpPage/Loader.vue'
import icon from '@components/icons/icon.vue'
import REGEX from '@src/constants/regex-constants.js'
import { mapState } from 'vuex'
export default {
  components: {
    UiOnClickOutside,
    Loader,
    icon,
  },
  mixins: [tooltipMixin],
  model: {
    prop: 'value',
    event: 'updateModel',
  },
  props: {
    icon: { type: Boolean, default: false },
    childClass: { type: String, default: '' },
    width: { type: [String, Number], default: '15.5' },
    height: { type: [String, Number], default: '19.617' },
    tooltipProp: {
      type: Boolean,
      default: false,
    },
    tooltipDesc: {
      type: String,
      default: 'toolTipInfo.SAMPLE_TEXT',
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 3,
    },
    resize: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: 'enterHere',
    },
    type: {
      type: String,
      default: 'text',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: 'autocomplete',
    },
    id: {
      type: String,
      default: 'input',
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    isVerified: {
      type: [Boolean, String],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isComponentInLoading: {
      type: Boolean,
      default: false,
    },
    isTranslated: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true,
    },
    modelValue: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    checkIsDataValid: {
      type: Boolean,
      default: false,
    },
    passwordValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hostName: window.location.hostname,
      fieldValue: '',
      showText: false,
      required: false,
      dashboard: 'dashboard',
      placeholderValue: '',
      clearText: false,
      forceRender: 0,
    }
  },
  computed: {
    ...mapState(['currentSelectedLanguage']),
   getHostName() {
      return this.hostName.replace(/^www\./, '');
    },
    fieldType() {
      let className = ''
      if (this.type === 'date') {
        className = 'DatePickerInput'
      }
      return className
    },
    iconType() {
      let iconName
      switch (this.type) {
        case 'email':
          iconName = 'email'
          break
        default:
          iconName
          break
      }
      return iconName
    },
    inputHeight() {
      return this.type === 'textarea' ? 'h-auto' : 'h-14'
    },
  },
  watch: {
    modelValue: {
      handler(val) {
        this.fieldValue =
          this.currentSelectedLanguage.short === 'ar' ? val.toString().toArabicDigits() : val

        // commented code use for arabic language in future
        //   if(this.currentSelectedLanguage.short === 'ar')this.fieldValue = val.toString().toArabicDigits()
        //  else this.fieldValue = val
      },
    },
    checkIsDataValid: {
      handler(value) {
        if (value) this.checkValidation()
      },
    },
  },

  methods: {
    blurInput() {
      if (!this.fieldValue) {
        this.checkValidation()
      }
    },
    /**
     * @description function to be called on input value change
     */
    updateInput(ev) {
      if (this.type === 'file' && ev.target.files) {
        for (let i = 0; i < ev.target.files.length; i++) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.$emit('change', ev.target.files[i], e.target.result, ev.target.files[0].name)
          }
          reader.readAsDataURL(ev.target.files[i])
        }
        return
      }
      // Check validation if rules exists
      if (this.rules) this.checkValidation()

      this.$emit('update:modelValue', this.fieldValue.toDigits())
      this.$emit('change', this.name, this.fieldValue.toDigits())
    },
    isError(regx, message) {
      if (this.fieldValue && !regx.test(this.fieldValue)) {
        this.emitError(message)
        return true
      }
      return false
    },
    emitError(message) {
      let fieldName = this.name === '' ? this.title : this.name
      this.$emit('emitError', `${fieldName} ${message}`)
    },
    checkValidation() {
      let rules = this.rules.split('|')

      if (!this.fieldValue && this.rules.includes('required')) {
        this.$emit('emitError', `${this.name === '' ? this.title : this.name} is required`)
        return
      }
      for (let rule of rules) {
        if (rule === 'email' && this.isError(REGEX.EMAIL, 'is invalid')) return
        if (rule === 'alpha' && this.isError(REGEX.ALPHABET_ONLY, 'contains only letters')) return
        if (
          rule === 'intituteDomain' &&
          this.isError(REGEX.INTITUTE_DOMAIN_REG, 'in special characters not allowed')
        )
          return
        if (rule === 'alpha_spaces' && this.isError(REGEX.ALPHABET_SPACES, 'contains only letters'))
          return
        if (
          rule === 'spacesNotAllowed' &&
          this.isError(REGEX.SPACE_NOT_ALLOWED, 'space is not allowed')
        )
          return
        if (rule === 'number' && this.isError(REGEX.ONLY_DECIMALS, 'contains only number')) return
        if (rule === 'password') {
          if (this.fieldValue.length < 8) {
            this.emitError('must be at least 8 characters')
            return
          }
          this.$emit('emitError', '')
        }
        if (rule === 'confirm_password') {
          if (this.fieldValue !== this.passwordValue) {
            this.emitError('does not match')
            return
          }
          this.$emit('emitError', '')
        }
        if (rule.includes('min')) {
          const minArray = rules.find((item) => item.startsWith('min')).split(':')
          const minChr = minArray[1]
          if (this.fieldValue.length < minChr) {
            this.emitError(`must be at least ${minChr} characters`)
            return
          }
          this.$emit('emitError', '')
        }
        if (rule.includes('max')) {
          const maxArray = rules.find((item) => item.startsWith('max')).split(':')
          const maxChr = maxArray[1]
          if (this.fieldValue.length > maxChr) {
            this.emitError(`must be at least ${maxChr} characters`)
            return
          }
          this.$emit('emitError', '')
        } else {
          this.$emit('emitError', '')
        }
      }
    },
    emitBlurEvent(e) {
      this.$emit('blurEvent', e)
    },
    showPass(event) {
      const passElm = event.target.closest('.input-field').querySelector('input')
      if (passElm.type === 'text') {
        passElm.type = 'password'
        this.showText = false
      } else {
        passElm.type = 'text'
        this.showText = true
      }
      this.forceRender++
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@src/design/auth.scss';
.DatePickerInput::-webkit-inner-spin-button,
.DatePickerInput::-webkit-calendar-picker-indicator {
  opacity: 0;
  -webkit-appearance: none;
}
</style>
